import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../store/slices/authSlice";
import type { AppDispatch, RootState } from "../../store";
import {
  HomeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ChartBarIcon,
  KeyIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  ChevronDownIcon,
  ShoppingBagIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
  BoltIcon,
  GlobeAltIcon,
  GiftIcon,
  BeakerIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import NotificationsDropdown from "../notifications/NotificationsDropdown";

interface DashboardLayoutProps {
  userType?: "creator" | "patron";
  children?: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  userType = "creator",
  children,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useSelector((state: RootState) => state.auth);

  // Close profile menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen]);

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate("/");
    } catch (error) {
      // Even if logout fails on backend, redirect to home
      navigate("/");
    }
  };

  // Navigation items based on user type
  const creatorNavItems = [
    {
      name: "Dashboard",
      icon: HomeIcon,
      href: "/dashboard",
      current: location.pathname === "/dashboard",
    },
    {
      name: "Patrons",
      icon: UserGroupIcon,
      href: "/dashboard/patrons",
      current: location.pathname.includes("/dashboard/patrons"),
    },
    {
      name: "Posts",
      icon: DocumentTextIcon,
      href: "/dashboard/posts",
      current: location.pathname.includes("/dashboard/posts"),
    },
    {
      name: "Tier Management",
      icon: CurrencyDollarIcon,
      href: "/dashboard/tiers",
      current: location.pathname.includes("/dashboard/tiers"),
    },
    {
      name: "Analytics",
      icon: ChartBarIcon,
      href: "/dashboard/analytics",
      current: location.pathname.includes("/dashboard/analytics"),
    },
    {
      name: "Forecasting",
      icon: ChartBarIcon,
      href: "/dashboard/forecasting",
      current: location.pathname.includes("/dashboard/forecasting"),
    },
    {
      name: "Cohorts",
      icon: UserGroupIcon,
      href: "/dashboard/cohorts",
      current: location.pathname.includes("/dashboard/cohorts"),
    },
    {
      name: "Geographic",
      icon: GlobeAltIcon,
      href: "/dashboard/geographic",
      current: location.pathname.includes("/dashboard/geographic"),
    },
    {
      name: "API Keys",
      icon: KeyIcon,
      href: "/dashboard/api-keys",
      current: location.pathname.includes("/dashboard/api-keys"),
    },
    {
      name: "Integrations",
      icon: BoltIcon,
      href: "/creator/integrations",
      current: location.pathname.includes("/creator/integrations"),
    },
    {
      name: "Tips",
      icon: CurrencyDollarIcon,
      href: "/dashboard/tips",
      current: location.pathname.includes("/dashboard/tips"),
    },
    {
      name: "Commissions",
      icon: CurrencyDollarIcon,
      href: "/commissions",
      current: location.pathname.includes("/commissions"),
    },
    {
      name: "A/B Testing",
      icon: BeakerIcon,
      href: "/dashboard/ab-testing",
      current: location.pathname.includes("/dashboard/ab-testing"),
    },
    {
      name: "Referrals",
      icon: UserGroupIcon,
      href: "/dashboard/referrals",
      current: location.pathname.includes("/dashboard/referrals"),
    },
    {
      name: "Shop",
      icon: CurrencyDollarIcon,
      href: "/dashboard/commissions",
      current: location.pathname.includes("/dashboard/commissions") && !location.pathname.includes("/requests"),
    },
    {
      name: "Payouts",
      icon: CurrencyDollarIcon,
      href: "/dashboard/payouts",
      current: location.pathname.includes("/dashboard/payouts"),
    },
    {
      name: "Shop",
      icon: ShoppingBagIcon,
      href: "/dashboard/shop",
      current: location.pathname.includes("/dashboard/shop"),
    },
    {
      name: "Livestreams",
      icon: VideoCameraIcon,
      href: "/dashboard/livestreams",
      current: location.pathname.includes("/dashboard/livestreams"),
    },
    {
      name: "KYC",
      icon: ShieldCheckIcon,
      href: "/dashboard/kyc",
      current: location.pathname.includes("/dashboard/kyc"),
    },
    {
      name: "Statements",
      icon: DocumentTextIcon,
      href: "/dashboard/statements",
      current: location.pathname.includes("/dashboard/statements"),
    },
    {
      name: "Team Members",
      icon: UserGroupIcon,
      href: "/dashboard/team",
      current: location.pathname.includes("/dashboard/team"),
    },
    {
      name: "Physical Benefits",
      icon: ShoppingBagIcon,
      href: "/dashboard/physical-benefits",
      current: location.pathname.includes("/dashboard/physical-benefits"),
    },
    {
      name: "Moderation",
      icon: ShieldCheckIcon,
      href: "/dashboard/moderation",
      current: location.pathname.includes("/dashboard/moderation"),
    },
    {
      name: "Reports",
      icon: DocumentTextIcon,
      href: "/dashboard/reports",
      current: location.pathname.includes("/dashboard/reports"),
    },
  ];

  const patronNavItems = [
    {
      name: "Dashboard",
      icon: HomeIcon,
      href: "/dashboard",
      current: location.pathname === "/dashboard",
    },
    {
      name: "Messages",
      icon: ChatBubbleLeftRightIcon,
      href: "/dashboard/messages",
      current: location.pathname.includes("/dashboard/messages"),
    },
    {
      name: "Group Chats",
      icon: ChatBubbleLeftRightIcon,
      href: "/dashboard/group-chat",
      current: location.pathname.includes("/dashboard/group-chat"),
    },
    {
      name: "Tip History",
      icon: CurrencyDollarIcon,
      href: "/dashboard/tips/history",
      current: location.pathname.includes("/dashboard/tips/history"),
    },
    {
      name: "Gift Memberships",
      icon: GiftIcon,
      href: "/dashboard/gifts",
      current: location.pathname.includes("/dashboard/gifts"),
    },
    {
      name: "Commissions",
      icon: CurrencyDollarIcon,
      href: "/commissions",
      current: location.pathname.includes("/commissions"),
    },
    {
      name: "Referrals",
      icon: ShareIcon,
      href: "/dashboard/referrals",
      current: location.pathname.includes("/dashboard/referrals"),
    },
    {
      name: "My Memberships",
      icon: UserGroupIcon,
      href: "/dashboard/memberships",
      current: location.pathname.includes("/dashboard/memberships"),
    },
    {
      name: "Payments",
      icon: CurrencyDollarIcon,
      href: "/dashboard/payments",
      current: location.pathname.includes("/dashboard/payments"),
    },
    {
      name: "Payment Methods",
      icon: CurrencyDollarIcon,
      href: "/dashboard/payment-methods",
      current: location.pathname.includes("/dashboard/payment-methods"),
    },
    {
      name: "Discover Creators",
      icon: PhotoIcon,
      href: "/dashboard/discover",
      current: location.pathname.includes("/dashboard/discover"),
    },
    {
      name: "My Activity",
      icon: DocumentTextIcon,
      href: "/dashboard/activity",
      current: location.pathname.includes("/dashboard/activity"),
    },
    {
      name: "Support",
      icon: ChatBubbleLeftRightIcon,
      href: "/dashboard/support",
      current: location.pathname.includes("/dashboard/support"),
    },
  ];

  const navItems = userType === "creator" ? creatorNavItems : patronNavItems;

  // Quick actions for creators
  const quickActions =
    userType === "creator"
      ? [
          {
            name: "New Post",
            icon: DocumentTextIcon,
            action: () => navigate("/dashboard/posts/new"),
          },
          {
            name: "Upload Video",
            icon: VideoCameraIcon,
            action: () => navigate("/dashboard/posts/new?type=video"),
          },
          {
            name: "Upload Audio",
            icon: MusicalNoteIcon,
            action: () => navigate("/dashboard/posts/new?type=audio"),
          },
          {
            name: "Upload Image",
            icon: PhotoIcon,
            action: () => navigate("/dashboard/posts/new?type=image"),
          },
        ]
      : [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--patreon-cream)' }}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-white text-black px-3 py-2 rounded shadow"
      >
        Skip to main content
      </a>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 flex lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`
        fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out bg-[color:var(--patreon-card-bg)] shadow-[var(--patreon-card-shadow)]
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0
      `}
        role="navigation"
        aria-label="Dashboard navigation"
      >
        <div className="flex flex-col h-full">
          {/* Logo and close button */}
          <div className="flex items-center justify-between h-16 px-6 border-b flex-shrink-0" style={{ borderColor: 'var(--patreon-card-border)' }}>
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full mr-3" style={{ background: 'var(--patreon-btn-primary-bg)' }}></div>
              <span className="text-xl font-bold text-gray-900">Patreon</span>
            </div>
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close sidebar">
              <XMarkIcon className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          {/* User info */}
          <div className="px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--patreon-card-border)' }}>
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                {user?.firstName?.[0] || user?.email?.[0] || "U"}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.email || "User"}
                </p>
                <p className="text-xs text-gray-500 capitalize">{userType}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto min-h-0" aria-label="Primary">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200
                    ${
                      item.current
                        ? "text-[color:var(--patreon-orange)] border-r-2"
                        : "text-gray-600 hover:bg-[color:var(--patreon-card-bg)] hover:text-gray-900"
                    }
                  `}
                  aria-current={item.current ? 'page' : undefined}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </button>
              );
            })}
          </nav>

          {/* Quick actions (creators only) */}
          {quickActions.length > 0 && (
            <div className="px-4 pb-6">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Quick Actions
              </div>
              <div className="space-y-1">
                {quickActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.name}
                      onClick={() => {
                        action.action();
                        setSidebarOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors duration-200"
                    >
                      <Icon className="w-4 h-4 mr-3" />
                      {action.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* User menu */}
          <div className="border-t border-gray-200 p-4 flex-shrink-0">
            <button
              onClick={() => navigate("/dashboard/settings")}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors duration-200 mb-2"
            >
              <CogIcon className="w-5 h-5 mr-3" />
              Settings
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top navigation bar */}
        <header className="bg-white shadow-sm border-b border-gray-200" role="banner">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Mobile menu button */}
              <button
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open sidebar"
                aria-expanded={sidebarOpen}
              >
                <Bars3Icon className="w-6 h-6 text-gray-500" />
              </button>

              {/* Spacer for desktop */}
              <div className="hidden lg:block"></div>

              {/* Right side buttons */}
              <div className="flex items-center space-x-4 ml-auto">
                {/* Notifications */}
                <NotificationsDropdown />

                {/* User menu */}
                <div className="relative" ref={profileMenuRef}>
                  <button 
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                    aria-label="User menu"
                    aria-expanded={profileMenuOpen}
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {user?.firstName?.[0] || user?.email?.[0] || "U"}
                    </div>
                    <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                  </button>

                  {/* Profile dropdown menu */}
                  {profileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">
                            {user?.firstName && user?.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user?.email || "User"}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        </div>
                        <button
                          onClick={() => {
                            navigate("/dashboard");
                            setProfileMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                          <HomeIcon className="w-4 h-4 mr-2" />
                          Dashboard
                        </button>
                        <button
                          onClick={() => {
                            navigate("/dashboard/settings");
                            setProfileMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                        >
                          <CogIcon className="w-4 h-4 mr-2" />
                          Settings
                        </button>
                        <div className="border-t border-gray-100 my-1"></div>
                        <button
                          onClick={() => {
                            handleLogout();
                            setProfileMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                        >
                          <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" className="flex-1" tabIndex={-1} role="main" aria-label="Main content">
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
